
import { Link } from 'react-router-dom';
import { Home, MapPin, Plus, LogIn, Menu, X, Zap, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsIOS } from '@/hooks/use-ios';
import { fadeIn, buttonHover, linkHover } from '@/components/ui/animations';
import { useAuth } from '@/contexts/AuthContext';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  const { user, signOut } = useAuth();

  const navLinks = [
    { name: 'Home', href: '/', icon: <Home className="w-4 h-4 mr-2" /> },
    { name: 'Find Chargers', href: '/', icon: <MapPin className="w-4 h-4 mr-2" /> },
    { name: 'Host Your Charger', href: '/host-charger', icon: <Plus className="w-4 h-4 mr-2" /> },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (isMobile) {
    return (
      <div className={`sticky top-0 z-50 w-full bg-background/80 backdrop-blur-sm border-b ${fadeIn} ${isIOS ? 'ios-nav-padding' : ''}`}>
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 touch-manipulation">
            <Zap className="h-6 w-6 text-primary" />
            <span className="font-bold text-xl">ChargeShare</span>
          </Link>
          
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className={`hover:bg-accent ${buttonHover} touch-manipulation`}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className={`p-0 ${isIOS ? 'ios-nav-padding ios-bottom-padding' : ''}`}>
              <div className="flex flex-col h-full">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Zap className="h-6 w-6 text-primary" />
                      <span className="font-bold text-xl">ChargeShare</span>
                    </div>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <X className="h-5 w-5" />
                      </Button>
                    </SheetTrigger>
                  </div>
                </div>
                <nav className="flex-1 p-4">
                  <ul className="space-y-2">
                    {navLinks.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`flex items-center p-2 rounded-md ${linkHover} hover:bg-accent`}
                          onClick={() => setIsOpen(false)}
                        >
                          {link.icon}
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div className="p-4 border-t">
                  {user ? (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground">
                        Signed in as {user.email}
                      </div>
                      <Button className="w-full" variant="outline" onClick={handleSignOut}>
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  ) : (
                    <Button className="w-full" asChild>
                      <Link to="/login">
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    );
  }

  return (
    <div className={`sticky top-0 z-50 w-full bg-background/80 backdrop-blur-sm border-b ${fadeIn} ${isIOS ? 'ios-nav-padding' : ''}`}>
      <div className="container flex h-14 items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <Zap className="h-6 w-6 text-primary" />
          <span className="font-bold text-xl">ChargeShare</span>
        </Link>
        
        <nav className="flex items-center space-x-6 text-sm font-medium">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={`flex items-center transition-colors ${linkHover}`}
            >
              {link.icon}
              {link.name}
            </Link>
          ))}
        </nav>
        
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={buttonHover}>
                <User className="w-4 h-4 mr-2" />
                Account
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-muted-foreground">
                {user.email}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button asChild className={buttonHover}>
            <Link to="/login">
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
